import { UpsellInsurance, UpsellInsuranceType, UpsellService, UpsellServiceType } from '@types';
import {
    IPriceInsurance,
    IProducts,
    IShipment,
    ProShipment,
    Shipment,
    ShipmentInsurance,
} from '@packlink/packlink-sdk';

import { ServiceUtils } from './ServiceUtils';

export type ProductOptionsInsurances = { [K in UpsellInsuranceType]?: UpsellInsurance };
export type ProductOptionsServices = { [K in UpsellServiceType]?: UpsellService };

export class UpsellUtils {
    static computeAvailableUpsells(
        products: IProducts,
        printInStore: boolean,
        shipment: Shipment,
    ): ProductOptionsServices {
        const services: ProductOptionsServices = {};

        const { cashOnDelivery, additionalHandling, adultSignature, proofOfDelivery } = products;

        const isAdditionalHandlingSelectedFromCheckoutShipment = shipment.upsales?.additionalHandling?.available;
        const isAdditionalHandlingAvailable = isAdditionalHandlingSelectedFromCheckoutShipment || additionalHandling;

        if (cashOnDelivery) {
            services[UpsellServiceType.CASH_ON_DELIVERY] = cashOnDelivery;
        }

        if (printInStore) {
            services[UpsellServiceType.PRINT_IN_STORE] = {
                totalPrice: 0,
            };
        }

        if (proofOfDelivery) {
            services[UpsellServiceType.PROOF_OF_DELIVERY] = proofOfDelivery;
        }

        if (adultSignature) {
            services[UpsellServiceType.ADULT_SIGNATURE] = adultSignature;
        }

        if (isAdditionalHandlingAvailable) {
            services[UpsellServiceType.ADDITIONAL_HANDLING] = additionalHandling;
        }

        return services;
    }

    // All these methods that depends on shipmentData will be handled in the next iteration of InsuranceRefactoring

    static computeAvailableInsurances(
        shipmentData: ProShipment['data'],
        insurance?: IPriceInsurance | ShipmentInsurance,
    ): UpsellInsuranceType[] {
        const availableInsurances: UpsellInsuranceType[] = [];
        const baseCoverage = insurance?.baseCoverage ?? 0;
        const maxCoverage = insurance?.maxCoverage;
        const contentValue = shipmentData.content?.value ?? 0;

        if (baseCoverage === 0) {
            return [...availableInsurances, UpsellInsuranceType.PACKLINK_INSURANCE, UpsellInsuranceType.NO_INSURANCE];
        } else {
            availableInsurances.push(UpsellInsuranceType.STANDARD);

            const isValueAboveBaseCoverage = contentValue && contentValue > baseCoverage;
            const isBaseCoverageBelowMaxCoverage = contentValue && maxCoverage && baseCoverage < maxCoverage;

            if ((isValueAboveBaseCoverage && isBaseCoverageBelowMaxCoverage) || !contentValue) {
                availableInsurances.push(UpsellInsuranceType.ENHANCED);
            }
        }
        return availableInsurances;
    }

    static getShipmentInsurance(
        shipmentData: ProShipment['data'],
        insurance?: IPriceInsurance | ShipmentInsurance,
    ): UpsellInsuranceType {
        const baseCoverage = insurance?.baseCoverage ?? 0;
        const hasBaseCoverage = baseCoverage > 0;
        const hasInsurance = shipmentData.upsales?.insurance?.available;
        const contentValue = shipmentData.content?.value ?? 0;

        if (hasInsurance && hasBaseCoverage && contentValue > baseCoverage) {
            return UpsellInsuranceType.ENHANCED;
        } else if (hasInsurance) {
            return UpsellInsuranceType.PACKLINK_INSURANCE;
        } else if (hasBaseCoverage) {
            return UpsellInsuranceType.STANDARD;
        } else {
            return UpsellInsuranceType.NO_INSURANCE;
        }
    }

    // This file has many methods handling insurance logic because the insurance data comes from the unrefactored shipments endpoint
    static getDefaultCheckoutInsuranceType(
        baseCoverage: number,
        upsales?: ProShipment['data']['upsales'],
    ): UpsellInsuranceType | undefined {
        const hasInsuredSelected = upsales?.insurance?.available;

        if (baseCoverage) {
            if (hasInsuredSelected) {
                return UpsellInsuranceType.ENHANCED;
            }
        }
        if (hasInsuredSelected) {
            return UpsellInsuranceType.PACKLINK_INSURANCE;
        }
        return undefined;
    }

    static getShipmentUpsellTypes(shipmentData: ProShipment['data']): UpsellServiceType[] {
        const upsellsType: UpsellServiceType[] = [];
        const adultSignature = shipmentData.upsales?.adultSignature?.available;
        const additionalHandling = shipmentData.upsales?.additionalHandling?.available;
        const printInStore = shipmentData.upsales?.printInStore?.available;
        const proofOfDelivery = shipmentData.upsales?.proofOfDelivery?.available;

        adultSignature && upsellsType.push(UpsellServiceType.ADULT_SIGNATURE);
        additionalHandling && upsellsType.push(UpsellServiceType.ADDITIONAL_HANDLING);
        printInStore && upsellsType.push(UpsellServiceType.PRINT_IN_STORE);
        proofOfDelivery && upsellsType.push(UpsellServiceType.PROOF_OF_DELIVERY);

        return upsellsType;
    }

    static setShipmentInsurance(
        shipmentData: ProShipment['data'],
        products: IProducts | undefined,
        hasInsurance: boolean,
    ): Shipment {
        if (!products?.insurance) {
            return shipmentData;
        }
        const shipmentDataJSON = shipmentData.toJSON();
        const canUpdateInsurance = products?.insurance?.isEnabled;

        const newShipmentData: IShipment = {
            ...shipmentDataJSON,
            upsales: {
                ...shipmentDataJSON.upsales,
                insurance: {
                    //If insurance is disabled, hold the previous state
                    available: canUpdateInsurance ? hasInsurance : shipmentDataJSON.upsales?.insurance?.available,
                    amount: products?.insurance.protection?.value,
                },
                proofOfDelivery: {
                    available:
                        canUpdateInsurance && hasInsurance
                            ? !!products?.proofOfDelivery?.isSelected // If there is insurance, proof of delivery is always false
                            : shipmentDataJSON.upsales?.proofOfDelivery?.available, // If there is no insurance, hold the previous pod state
                },
            },
        };

        return Shipment.deserialize(newShipmentData);
    }

    static getShipmentStatusProtection(shipments: ProShipment[]) {
        let isProtectedShipment = false;
        let someShipmentPartiallyProtected = false;

        shipments?.forEach((shipment) => {
            const insuranceType = UpsellUtils.getShipmentInsurance(shipment.data, shipment.data.upsales?.insurance);
            const availableOptions = UpsellUtils.computeAvailableInsurances(
                shipment.data,
                shipment.data.upsales?.insurance,
            );
            const isEnhancedOptionAvailable = availableOptions?.includes(UpsellInsuranceType.ENHANCED);

            const hasNoInsuranceSelected = insuranceType === UpsellInsuranceType.NO_INSURANCE;

            const serviceException = ServiceUtils.getServiceTranslationKey(
                'insuranceException',
                shipment.data.service?.carrierName,
                shipment.data.service?.name,
            );

            if (
                serviceException ||
                (!isEnhancedOptionAvailable && !hasNoInsuranceSelected) ||
                insuranceType === UpsellInsuranceType.ENHANCED
            ) {
                isProtectedShipment = true;
            }
            if (
                insuranceType !== UpsellInsuranceType.ENHANCED &&
                isEnhancedOptionAvailable &&
                !hasNoInsuranceSelected
            ) {
                someShipmentPartiallyProtected = true;
            }
        });

        return { isProtectedShipment, someShipmentPartiallyProtected };
    }
}
